import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, CardContent, Grid } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircle from '@material-ui/icons/CheckCircle';
import EditIcon from '@material-ui/icons/Edit';
import { makeStyles } from '@material-ui/core/styles';

import {
  BulkUpdateContentMutationVariables,
  ContentApprovalTypes,
  SearchContentSortFields,
  SortDirections,
  useBulkUpdateContentMutation,
  UserFeedFollowGroupFragment,
} from 'api';
import { useContent, useNotify } from 'hooks';

import { Date } from 'features/content/widgets';
import useBulkContentUpdateMutation from 'features/admin/updaters/useBulkContentUpdate';

interface IProps {
  selectedGroup: UserFeedFollowGroupFragment;
  showRemove?: boolean;
}

const useStyles = makeStyles(theme => ({
  approvalButton: {
    padding: theme.spacing(1, 2),

    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
    },
  },
  removalButton: {
    padding: theme.spacing(1, 2),

    '&:hover': {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.main,
    },
  },
  editButton: {
    padding: theme.spacing(1, 2),
    '&:hover': {
      backgroundColor: theme.palette.grey[300],
    },
  },
}));

const Approval: FC<IProps> = ({ showRemove, selectedGroup }) => {
  const classes = useStyles();
  const notify = useNotify();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { content_id, publish_at } = useContent();

  const variables: BulkUpdateContentMutationVariables = {
    bulk_update: {
      approval: ContentApprovalTypes.Submitted,
      content_group_list: [
        {
          group_id: selectedGroup.id,
          content_id: content_id,
        },
      ],
    },
  };

  const updateContentMutationOptions = useBulkContentUpdateMutation({
    search_content: {
      approval: showRemove ? ContentApprovalTypes.Submitted : ContentApprovalTypes.Removed,
      group_list: [selectedGroup.id],
      sort_by: [
        {
          direction: SortDirections.Desc,
          field: SearchContentSortFields.CreatedAt,
        },
      ],
    },
  });

  const { mutate: updateContentMutation } = useBulkUpdateContentMutation({
    ...updateContentMutationOptions,
    onSuccess,
  });

  function onSuccess() {
    if (variables.bulk_update.approval === ContentApprovalTypes.Approved) {
      notify.info({ message: t('content:postApproved') });
      return;
    }

    notify.info({ message: t('content:postRemoved') });
  }

  function handleEdit() {
    return navigate(`/post/edit/${content_id}`);
  }

  function handleContentRemoval() {
    variables.bulk_update.approval = ContentApprovalTypes.Removed;
    updateContentMutation(variables);
  }

  function handleContentApproval() {
    variables.bulk_update.approval = ContentApprovalTypes.Approved;
    updateContentMutation(variables);
  }

  return (
    <CardContent>
      <Grid container alignItems="center" justify="space-between">
        <Grid item xs>
          <Date contentId={content_id} publishAt={publish_at} />
        </Grid>
        <Grid item>
          <Grid container>
            {showRemove && (
              <Grid item>
                <Button
                  className={classes.removalButton}
                  variant="text"
                  startIcon={<CancelIcon color="error" />}
                  onClick={handleContentRemoval}
                >
                  {t('common:remove')}
                </Button>
              </Grid>
            )}
            <Grid item>
              <Button
                className={classes.editButton}
                variant="text"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                {t('common:edit')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                className={classes.approvalButton}
                variant="text"
                startIcon={<CheckCircle color="primary" />}
                onClick={handleContentApproval}
              >
                {t('common:approve')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </CardContent>
  );
};

Approval.defaultProps = {
  showRemove: false,
};

export default Approval;
